// UserCircle.tsx — wiederverwendbarer Avatar-Circle für die Navigation
// (SP2 der Profil-Seite). EINE Komponente, zwei Andockpunkte über `placement`:
//   - 'topbar'  → 36px + weißer Rand (Optik wie zuvor in NavigationLayout)
//   - 'sidebar' → 32px ohne weißen Rand (neuer Desktop/Tablet-Sidebar-Footer)
// Inhalt = Microsoft/Entra-Profilfoto (falls vorhanden), sonst Kürzel/Initialen
// aus dem Session-User (zentrale SP1-Helfer in `pages/profile/userDisplay.ts`,
// damit Header-Avatar und Nav-Circle nie driften). Foto wird authentifiziert
// per useBlobUrl geladen; jeder Fehler (kein Foto/Consent/Netz) → Initialen.

import React from "react";
import { useAuth } from "../context/AuthContext";
import { useBlobUrl } from "../hooks/useBlobUrl";
import {
  displayName,
  userKuerzel,
  type AuthUserLike,
} from "../pages/profile/userDisplay";

export interface UserCircleProps {
  /** Steuert Default-Größe + Default-Rahmen. 'topbar' = 36px + weißer Rand;
   *  'sidebar' = 32px ohne weißen Rand. */
  placement: "sidebar" | "topbar";
  /** Überschreibt die placement-Default-Größe (px). */
  size?: number;
  /** Klick auf den Circle (öffnet i.d.R. das Popover). */
  onClick?: (e: React.MouseEvent) => void;
  /** Tooltip/aria-label; Default aus displayName + Rollen. */
  title?: string;
  /** Optional eigener User; Default useAuth().user. */
  user?: AuthUserLike;
  /** User-Id für das Profilfoto. Default = Session-User (useAuth().user.id).
   *  Bei explizit übergebenem `user` (z. B. Listen) hier mitgeben, sonst gibt's
   *  nur Initialen (AuthUserLike trägt keine id). `null` = nie ein Foto laden. */
  userId?: string | null;
  /** Optional ref-Weiterleitung an das Wurzel-<div> (für Popover-Trigger). */
  innerRef?: React.Ref<HTMLDivElement>;
}

/**
 * Runder Avatar: zeigt das Entra-Profilfoto, sonst Gold-Hintergrund (#ffa714) +
 * Aubergine-Text (#361836) mit den User-Initialen/Kürzeln. Reine Präsentation —
 * die Logout/Profil-Aktionen sitzen im umschließenden `UserMenuPopover`.
 */
export const UserCircle: React.FC<UserCircleProps> = ({
  placement,
  size,
  onClick,
  title,
  user,
  userId,
  innerRef,
}) => {
  const auth = useAuth();
  const roles = auth.roles ?? [];
  // Default-User aus der Session, falls kein expliziter übergeben wurde.
  const resolvedUser: AuthUserLike =
    user !== undefined ? user : (auth.user as AuthUserLike);

  // Foto-Id: explizite `userId`-Prop, sonst (nur im Session-Default-Fall) die
  // Id des eingeloggten Users. `null` schaltet das Foto bewusst ab.
  const resolvedUserId =
    userId !== undefined
      ? userId
      : user === undefined
        ? (auth.user?.id as string | undefined)
        : undefined;

  const photoRef = resolvedUserId
    ? `/users/${encodeURIComponent(resolvedUserId)}/photo`
    : null;
  const { url: photoUrl } = useBlobUrl(photoRef);

  // <img>-Ladefehler trotz erfolgreich geholtem Blob (korrupte Bytes o. ä.) →
  // ebenfalls auf Initialen zurückfallen. Reset, sobald sich die Quelle ändert.
  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => {
    setImgError(false);
  }, [photoUrl]);
  const showPhoto = !!photoUrl && !imgError;

  const dim = size ?? (placement === "topbar" ? 36 : 32);
  const label =
    title ?? `${displayName(resolvedUser)} (${roles.join(", ") || "Keine Rolle"})`;

  return (
    <div
      ref={innerRef}
      title={label}
      aria-label={label}
      onClick={onClick}
      style={{
        width: dim,
        height: dim,
        borderRadius: "50%",
        backgroundColor: "#ffa714",
        color: "#361836",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: dim <= 30 ? 12 : 13,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        userSelect: "none",
        flexShrink: 0,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        ...(placement === "topbar" ? { border: "2px solid #fff" } : null),
      }}
    >
      {showPhoto ? (
        <img
          src={photoUrl ?? undefined}
          alt=""
          onError={() => setImgError(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        userKuerzel(resolvedUser)
      )}
    </div>
  );
};

export default UserCircle;
